import { SrclangType, zSrclang } from "@yedalabs/schema";
import { z } from "zod";

export const videoWorkflowStepNames: readonly [
    VideoWorkflowStepName,
    ...VideoWorkflowStepName[],
] = [
    "processing",
    "captions",
    "chapters",
    // `interactions` runs before `translations` so that, when both are set up
    // together, the spoken-language interactions exist by the time the
    // translations step runs and can copy them into the translated languages
    // (when "translate interactions" is enabled).
    "interactions",
    "translations",
    "dubbings",
    // Embeds the (transcribed) video into selected chatbots' v2 knowledge.
    // Runs last — it only needs the spoken-language captions, which are ready
    // once the `captions` step completes.
    "chatbot-knowledge",
];
export const videoWorkflowStepNamesThatRequireCaptions: readonly [
    string,
    ...string[],
] = [
    "chapters",
    "interactions",
    "translations",
    "dubbings",
    "chatbot-knowledge",
];

export type VideoWorkflowStepName =
    | "processing"
    | "captions"
    | "chapters"
    | "interactions"
    | "translations"
    | "dubbings"
    | "chatbot-knowledge";

export type VideoWorkflowStepStatus =
    | "queue"
    | "in-progress"
    | "completed"
    | "failed";

// -------------
export type Voice =
    // | "alloy"
    // | "ash"
    // | "ballad"
    // | "coral"
    // | "echo"
    // | "fable"
    // | "nova"
    // | "onyx"
    // | "sage"
    // | "shimmer";
    | "Zephyr"
    | "Puck"
    | "Charon"
    | "Kore"
    | "Fenrir"
    | "Leda"
    | "Orus"
    | "Aoede"
    | "Callirrhoe"
    | "Autonoe"
    | "Enceladus"
    | "Iapetus"
    | "Umbriel"
    | "Algieba"
    | "Despina"
    | "Erinome"
    | "Algenib"
    | "Rasalgethi"
    | "Laomedeia"
    | "Achernar"
    | "Alnilam"
    | "Schedar"
    | "Gacrux"
    | "Pulcherrima"
    | "Achird"
    | "Zubenelgenubi"
    | "Vindemiatrix"
    | "Sadachbia"
    | "Sadaltager"
    | "Sulafat";
export const voices: [Voice, ...Voice[]] = [
    // "alloy",
    // "ash",
    // "ballad",
    // "coral",
    // "echo",
    // "fable",
    // "nova",
    // "onyx",
    // "sage",
    // "shimmer",
    "Zephyr",
    "Puck",
    "Charon",
    "Kore",
    "Fenrir",
    "Leda",
    "Orus",
    "Aoede",
    "Callirrhoe",
    "Autonoe",
    "Enceladus",
    "Iapetus",
    "Umbriel",
    "Algieba",
    "Despina",
    "Erinome",
    "Algenib",
    "Rasalgethi",
    "Laomedeia",
    "Achernar",
    "Alnilam",
    "Schedar",
    "Gacrux",
    "Pulcherrima",
    "Achird",
    "Zubenelgenubi",
    "Vindemiatrix",
    "Sadachbia",
    "Sadaltager",
    "Sulafat",
];
export const zVoice = z.enum(voices);
export const voiceDescriptions: Record<Voice, string> = {
    Zephyr: "Bright",
    Puck: "Upbeat",
    Charon: "Informative",
    Kore: "Firm",
    Fenrir: "Excitable",
    Leda: "Youthful",
    Orus: "Firm",
    Aoede: "Breezy",
    Callirrhoe: "Easy-going",
    Autonoe: "Bright",
    Enceladus: "Breathy",
    Iapetus: "Clear",
    Umbriel: "Easy-going",
    Algieba: "Smooth",
    Despina: "Smooth",
    Erinome: "Clear",
    Algenib: "Gravelly",
    Rasalgethi: "Informative",
    Laomedeia: "Upbeat",
    Achernar: "Soft",
    Alnilam: "Firm",
    Schedar: "Even",
    Gacrux: "Mature",
    Pulcherrima: "Forward",
    Achird: "Friendly",
    Zubenelgenubi: "Casual",
    Vindemiatrix: "Gentle",
    Sadachbia: "Lively",
    Sadaltager: "Knowledgeable",
    Sulafat: "Warm",
};

export const getVoiceLabel = (voice: Voice) =>
    `${voice} (${voiceDescriptions[voice]})`;

export type TTSModel =
    | "gemini-2.5-flash-preview-tts"
    | "gemini-2.5-pro-preview-tts"; //  "gpt-4o-mini-tts" | "tts-1" | "tts-1-hd";
export const ttsModels: [TTSModel, ...TTSModel[]] = [
    // "gpt-4o-mini-tts",
    // "tts-1",
    // "tts-1-hd",
    "gemini-2.5-flash-preview-tts",
    "gemini-2.5-pro-preview-tts",
];
export const zTTSModel = z.enum(ttsModels);

export const defaultVoice: Voice = "Zephyr";
export const defaultTTSModel: TTSModel = "gemini-2.5-flash-preview-tts";

export const LOMDA_DEFAULT_TTS_MODEL: TTSModel = "gemini-2.5-pro-preview-tts";

/**
 * Lomda narrator voice selection. The editor exposes a simple male/female
 * choice (Gemini TTS) rather than the full voice list. Female is the default
 * and maps to the historical lomda voice (Leda) for back-compat with audio
 * that was generated before the gender selector existed.
 */
export type NarratorGender = "male" | "female";
export const narratorGenders: [NarratorGender, ...NarratorGender[]] = [
    "female",
    "male",
];
export const LOMDA_FEMALE_VOICE: Voice = "Leda";
export const LOMDA_MALE_VOICE: Voice = "Fenrir"; // 'Iapetus' 'Sadeltager'
export const DEFAULT_NARRATOR_GENDER: NarratorGender = "female";

export const getLomdaVoiceForGender = (
    gender: NarratorGender | undefined
): Voice => (gender === "male" ? LOMDA_MALE_VOICE : LOMDA_FEMALE_VOICE);

/** Historical default lomda voice (female). Kept for back-compat references. */
export const LOMDA_DEFAULT_VOICE: Voice = LOMDA_FEMALE_VOICE;

export type Dubbings = {
    model: TTSModel;
    voice: Voice;
    languages: SrclangType[];
    instructions: string;
};
export const zDubbings = z.object({
    model: zTTSModel,
    voice: zVoice,
    languages: zSrclang.array(),
    instructions: z.string(),
});

export type ChatbotKnowledge = {
    /** Chatbots (v2) the video should be embedded into as a knowledge source. */
    chatbotIds: string[];
    /** When true the video is added with `noCite`, so the chatbot won't cite it. */
    hideReference: boolean;
};
export const zChatbotKnowledge = z.object({
    chatbotIds: z.string().array(),
    hideReference: z.boolean(),
});
// --------------------------

export type VideoWorkflowStep = {
    id: string;
    name: VideoWorkflowStepName;
    index: number;
    progress: number;
    status: VideoWorkflowStepStatus;
    error?: {
        keyName: string;
        defaultValue: string;
        openPaymentsModal?: boolean;
    };
    data?: {
        transcription?: Transcription;
        runpodEndpointId?: string;
        translations?: SrclangType[];
        /** When set on the translations step, the spoken-language interactions
         *  are copied into the languages being translated. */
        translateInteractions?: boolean;
        dubbings?: Dubbings;
        chatbotKnowledge?: ChatbotKnowledge;
    };
};
/** i18n keys — use with `t()` on the client; matches `localization.ts` entries. */
export const videoWorkflowStepLabelKeyByName: Record<
    VideoWorkflowStepName,
    string
> = {
    processing: "funnels.ai-upload-videos.workflow-step.processing",
    captions: "funnels.ai-upload-videos.workflow-step.captions",
    chapters: "funnels.ai-upload-videos.workflow-step.chapters",
    interactions: "funnels.ai-upload-videos.workflow-step.interactions",
    translations: "funnels.ai-upload-videos.workflow-step.translations",
    dubbings: "funnels.ai-upload-videos.workflow-step.dubbings",
    "chatbot-knowledge":
        "funnels.ai-upload-videos.workflow-step.chatbot-knowledge",
};

export type Transcription = {
    srclang: SrclangType;
    initialPrompt?: string;
    hotwords?: string[];
};
export const zTranscription = z.object({
    srclang: zSrclang,
    initialPrompt: z.string().optional(),
    hotwords: z.string().array().optional(),
});
export type TranscriptionClientState = {
    srclang?: SrclangType;
    initialPrompt?: string;
    hotwords?: string;
};
export const parseTranscriptionToState = (
    transcription?: Transcription
): TranscriptionClientState => {
    if (!transcription) return {};
    return {
        ...transcription,
        hotwords: transcription.hotwords?.join(", "),
    };
};
export const convertStateToTranscription = (
    srclang: SrclangType,
    state: TranscriptionClientState
): Transcription => {
    return {
        srclang,
        initialPrompt: state.initialPrompt,
        hotwords: state.hotwords
            ?.split(",")
            .map((word) => word.trim())
            .filter(Boolean),
    };
};

export const videoWorkflowCollectionName = "ai-video-workflow";
